// src/components/eventos/hooks/useEventos.js
import { useState, useEffect, useCallback, useRef } from 'react';

export const useEventos = (supabase, session) => {
    const [eventoAtivo, setEventoAtivo] = useState(null);
    const [proximoEvento, setProximoEvento] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const dadosAnterioresRef = useRef(null);

    const fetchEventos = useCallback(async () => {
        if (!supabase) {
            setError('Erro: Cliente Supabase não disponível');
            setIsLoading(false);
            return;
        }

        const primeiraExecucao = dadosAnterioresRef.current === null;
        if (primeiraExecucao) {
            setIsLoading(true);
        }
        setError(null);

        try {
            // Obter cliente_id do usuário (super_admin não tem, busca todos)
            const clienteId = session.user.user_metadata?.cliente_id;
            const isSuperAdmin = session.user.user_metadata?.perfil === 'super_admin';

            // Buscar evento em andamento
            let queryAtivo = supabase.from('eventos').select('*').eq('status', 'em_andamento');
            if (clienteId) queryAtivo = queryAtivo.eq('cliente_id', clienteId);
            let { data: eventoEmAndamento, error: errorAtivo } = await queryAtivo
                .order('data', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (errorAtivo && errorAtivo.code !== 'PGRST116') {
                throw errorAtivo;
            }

            const stringEventoAtual = JSON.stringify(eventoEmAndamento);
            const stringEventoAnterior = JSON.stringify(dadosAnterioresRef.current?.eventoAtivo);

            if (stringEventoAtual !== stringEventoAnterior) {
                setEventoAtivo(eventoEmAndamento || null);
                if (!dadosAnterioresRef.current) {
                    dadosAnterioresRef.current = {};
                }
                dadosAnterioresRef.current.eventoAtivo = eventoEmAndamento;
            }

            // Se não houver evento ativo, buscar o próximo agendado
            if (!eventoEmAndamento) {
                const _d = new Date(); const hoje = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;
                let queryProximo = supabase.from('eventos').select('*').eq('status', 'agendado').gte('data', hoje);
                if (clienteId) queryProximo = queryProximo.eq('cliente_id', clienteId);
                let { data: proximoAgendado, error: errorProximo } = await queryProximo
                    .order('data', { ascending: true })
                    .limit(1)
                    .maybeSingle();

                if (errorProximo && errorProximo.code !== 'PGRST116') {
                    throw errorProximo;
                }

                const stringProximoAtual = JSON.stringify(proximoAgendado);
                const stringProximoAnterior = JSON.stringify(dadosAnterioresRef.current?.proximoEvento);

                if (stringProximoAtual !== stringProximoAnterior) {
                    setProximoEvento(proximoAgendado || null);
                    if (!dadosAnterioresRef.current) {
                        dadosAnterioresRef.current = {};
                    }
                    dadosAnterioresRef.current.proximoEvento = proximoAgendado;
                }
            } else {
                setProximoEvento(null);
            }
        } catch (error) {
            console.error('Erro ao buscar eventos:', error);
            setError('Erro ao carregar eventos. Tentando novamente...');
        } finally {
            if (primeiraExecucao) {
                setIsLoading(false);
            }
        }
    }, [supabase, session]);

    useEffect(() => {
        fetchEventos();
        // Atualizar a cada 15 segundos
        const interval = setInterval(fetchEventos, 15000);
        return () => clearInterval(interval);
    }, [fetchEventos]);

    return {
        eventoAtivo,
        proximoEvento,
        isLoading,
        error,
        refetch: fetchEventos,
    };
};
